import { defineStore } from 'pinia'

export const useCheckoutFieldLimitsStore = defineStore('checkoutFieldLimits', {
    state: () => ({
        // Keyed by ExtendedField UUID (core_id), then option key
        // { [fieldCoreId]: { [optionKey]: { available: number|null, localUsage: number } } }
        fields: {},
    }),
    actions: {
        /**
         * Initialize from API response.
         * API excludes current order, so available = limit - used is the baseline.
         */
        initialize(apiData) {
            const fields = {}
            for (const [fieldCoreId, fieldData] of Object.entries(apiData)) {
                fields[fieldCoreId] = {}
                for (const [optionKey, optionData] of Object.entries(fieldData.options || {})) {
                    fields[fieldCoreId][optionKey] = {
                        available: optionData.limit !== null && optionData.limit !== undefined
                            ? optionData.limit - optionData.used
                            : null,
                        localUsage: 0,
                    }
                }
            }
            this.fields = fields
        },

        isFieldLimitsEnabled(fieldCoreId) {
            return fieldCoreId && this.fields[fieldCoreId] !== undefined
        },

        getEffectiveAvailability(fieldCoreId, optionKey) {
            const field = this.fields[fieldCoreId]
            if (!field || !field[optionKey]) {
                return null
            }
            const option = field[optionKey]
            if (option.available === null) {
                return null
            }
            return option.available - option.localUsage
        },

        isOptionDisabled(fieldCoreId, optionKey) {
            const availability = this.getEffectiveAvailability(fieldCoreId, optionKey)
            if (availability === null) {
                return false
            }
            return availability <= 0
        },

        /**
         * Recalculate local usage by scanning all current form data across all guests.
         * @param {Array} allGuestFormData - Array of { formData, fields } for each guest
         * @param {Function} getFieldPath - Function to resolve field to form path
         */
        recalculateLocalUsage(allGuestFormData, getFieldPath) {
            // Reset all localUsage to 0
            for (const fieldCoreId of Object.keys(this.fields)) {
                for (const optionKey of Object.keys(this.fields[fieldCoreId])) {
                    this.fields[fieldCoreId][optionKey].localUsage = 0
                }
            }

            // Count selections across all guests
            for (const { formData, fields: formFields } of allGuestFormData) {
                if (!formFields?.items || !formData) continue

                for (const item of formFields.items) {
                    const fieldCoreId = item.field?.core_id
                    if (!fieldCoreId || !this.fields[fieldCoreId]) continue

                    const path = getFieldPath(item)
                    if (!path) continue

                    const value = getNestedValue(formData, path)
                    if (value === null || value === undefined) continue

                    if (Array.isArray(value)) {
                        for (const selectedKey of value) {
                            if (this.fields[fieldCoreId][selectedKey] !== undefined) {
                                this.fields[fieldCoreId][selectedKey].localUsage++
                            }
                        }
                    } else if (typeof value === 'string' && this.fields[fieldCoreId][value] !== undefined) {
                        this.fields[fieldCoreId][value].localUsage++
                    }
                }
            }
        },

        getAvailabilityText(fieldCoreId, optionKey, t, isCurrentlySelected = false) {
            const availability = this.getEffectiveAvailability(fieldCoreId, optionKey)
            if (availability === null) {
                return ''
            }
            if (availability <= 0 && !isCurrentlySelected) {
                return `(${t('centerstage.live.limits.unavailable.select')})`
            }
            return `(${t('centerstage.live.limits.select.options.availability').replace(':num', String(availability))})`
        },
    },
})

/**
 * Navigate nested object using dot-notation path.
 */
function getNestedValue(obj, path) {
    if (!obj || !path) return null
    const keys = path.split('.')
    let current = obj
    for (const key of keys) {
        if (current === null || current === undefined) return null
        current = typeof current === 'object' ? current[key] : undefined
    }
    return current ?? null
}
